const { getOffsetTop } = window.BAO.Utils

class TextReveal extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    /* eslint-disable-next-line no-undef */
    if (Shopify.designMode) return

    this.isTicking = false
    this.lastKnownY = window.scrollY
    this.isVisible = false

    this.requestTick = this.requestTick.bind(this)
    this.updateEl = this.updateEl.bind(this)

    this.cachedWordEls = null
    this.cachedScrollPositions = null

    this.setupIntersectionObserver()
  }

  setupIntersectionObserver () {
    // eslint-disable-next-line compat/compat
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isVisible = entry.isIntersecting
        if (!entry.isIntersecting) {
          this.isTicking = false
        } else if (entry.isIntersecting) {
          this.requestTick()
        }
      },
      { rootMargin: '200px' },
    )
    this.observer.observe(this)
  }

  setupListeners () {
    super.setupListeners()

    this.onScroll = this.onScroll.bind(this)
    this.debouncedOnResize = window.BAO.Utils.debounce(() => {
      this.cachedScrollPositions = null
    }, 200)

    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.debouncedOnResize)
  }

  onScroll () {
    if (!this.isVisible) return

    this.lastKnownY = window.scrollY

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.revealText()
  }

  revealText () {
    if (!this.cachedScrollPositions) {
      const containerOffsetTop = getOffsetTop(this)
      this.cachedScrollPositions = {
        start: containerOffsetTop,
        end: containerOffsetTop + window.innerHeight / 2,
      }
    }
    const elementStartPoint = this.cachedScrollPositions.start
    const elementEndPoint = this.cachedScrollPositions.end

    if (!this.cachedWordEls) {
      this.cachedWordEls = this.querySelectorAll(this.els.word.selector)
    }
    const wordEls = this.cachedWordEls

    if (this.lastKnownY <= elementStartPoint) {
      wordEls.forEach((wordEl) => {
        wordEl.style.setProperty('--Reveal_Amount', '0%')
      })

      return
    }

    if (this.lastKnownY > elementEndPoint) {
      wordEls.forEach((wordEl) => {
        wordEl.style.setProperty('--Reveal_Amount', '100%')
      })

      return
    }

    const totalScrollAmount = elementEndPoint - elementStartPoint

    const scrollYAsAPercentageOfJourney =
      (this.lastKnownY - elementStartPoint) / totalScrollAmount

    wordEls.forEach((wordEl, index) => {
      const minimumPercent = index / wordEls.length
      const maximumPercent = (index + 1) / wordEls.length
      const isWithinRange =
        scrollYAsAPercentageOfJourney >= minimumPercent &&
        scrollYAsAPercentageOfJourney < maximumPercent

      if (isWithinRange) {
        const wordScrolledAmount =
          scrollYAsAPercentageOfJourney - minimumPercent
        const wordMaxAmount = maximumPercent - minimumPercent

        const wordProgress = wordScrolledAmount / wordMaxAmount

        const wordProgressAmount = Math.round(wordProgress * 100 * 10) / 10

        wordEl.style.setProperty('--Reveal_Amount', `${wordProgressAmount}%`)
      } else if (scrollYAsAPercentageOfJourney > maximumPercent) {
        wordEl.style.setProperty('--Reveal_Amount', '100%')
      } else {
        wordEl.style.setProperty('--Reveal_Amount', '0%')
      }
    })
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.debouncedOnResize)

    this.cachedWordEls = null
    this.cachedScrollPositions = null
  }
}

if (!customElements.get('text-reveal')) {
  customElements.define('text-reveal', TextReveal)
}
